import { useEffect, useMemo, useState } from "react"

import { XIcon, MessageCircleQuestion } from "lucide-react"
import { Select, Input, DefaultPopover, Separator, ToggleOptionGroup, ToggleOptionGroupItem, DefaultTooltip } from "twyne-new.ui"

import { useResponsive, useUtilities } from "@/modules/cms/views/post/components/builder/context"

import { useCraftProp } from "../utils"

import ApplyButtonPopover from "./components/ApplyButtonPopover"
import PositionInputs from "./components/PositionInputs"
import { ListContent, ListItem, ListLabel, ListWrapper } from "./components/list"

export default function ScrollPropertyWrapper() {
  const { device } = useResponsive()
  const { selectedUtilities } = useUtilities()

  return <>
    <ScrollProperty key={device + selectedUtilities} />
  </>
}

const ScrollProperty = () => {
  const [open, setOpen] = useState(false)
  const [scroll, setScroll] = useState()

  const { value: craftValue, setValue: setCraftValue } = useCraftProp('scroll')

  useEffect(() => {
    if (!open) {
      return setScroll(undefined)
    }

    if (scroll === undefined) {
      setScroll(craftValue || {})
    }
  }, [scroll, craftValue, open, setScroll])

  const handleApply = () => {
    setCraftValue(scroll)
  }

  const handleCancel = () => {
    setScroll()
    setOpen(false)
  }

  const handleChange = value => {
    setScroll(prev => ({ ...prev, ...value }))
  }

  return <>
    <InputTrigger onClick={() => setOpen(true)} value={craftValue} />
    <DefaultPopover
      wrapperProps={{ open, onOpenChange: setOpen }}
      trigger={<div></div>}
      triggerProps={{ asChild: true }}
      contentProps={{ className: 'w-96', side: 'left', align: 'center' }}
    >
      <div className="w-full">
        <div className='flex justify-between items-center'>
          Scroll

          <XIcon size='16' className='cursor-pointer' onClick={() => setOpen(false)} />
        </div>
        <Separator className='m-0 my-2' />

        <Inputs values={scroll} onChange={handleChange} />
      </div>

      <ApplyButtonPopover
        onApply={handleApply}
        onCancel={handleCancel}
        name='scroll'
        value={scroll}
      />
    </DefaultPopover>
  </>
}

const Inputs = ({ values, onChange }) => {
  if (!values) {
    return null
  }

  return <ListWrapper>
    <div className='flex flex-col gap-2'>

      <ListItem>
        <CustomListLabel title='Type' hint='Controls smooth or instant scrolling.' />
        <ListContent>
          <ToggleOptionGroup size='lg' value={values.behavior || ''} type='single' onValueChange={value => onChange({ behavior: value })}>
            <ToggleOptionGroupItem value='auto'>Auto</ToggleOptionGroupItem>
            <ToggleOptionGroupItem value='smooth'>Smooth</ToggleOptionGroupItem>
          </ToggleOptionGroup>
        </ListContent>
      </ListItem>

      <Separator className='m-0 my-2' />
      Snap
      <Snap values={values?.snap || {}} onChange={onChange} />

      <Separator className='m-0 my-2' />
      Sizing
      <Size values={values} onChange={onChange} />
    </div>
  </ListWrapper>
}

const Snap = ({ values, values: { align, stop, type }, onChange }) => <>
  <ListItem>
    <CustomListLabel title='Align' hint='Controls where items land when snapping.' />
    <ListContent>
      <Select
        options={SNAP_ALIGN}
        value={align}
        onChange={option => onChange({ snap: { ...values, align: option?.value } })}
        variant='accent'
        clearable
      />
    </ListContent>
  </ListItem>

  <ListItem>
    <CustomListLabel title='Stop' hint='Makes an item always stop on snap, even during fast scroll.' />
    <ListContent>
      <ToggleOptionGroup size='lg' value={stop || ''} type='single' onValueChange={value => onChange({ snap: { ...values, stop: value } })}>
        <ToggleOptionGroupItem value='normal'>Normal</ToggleOptionGroupItem>
        <ToggleOptionGroupItem value='always'>Always</ToggleOptionGroupItem>
      </ToggleOptionGroup>
    </ListContent>
  </ListItem>

  <ListItem>
    <CustomListLabel title='Type' hint='Controls how the container snaps while scrolling.' />
    <ListContent>
      <Select
        options={SNAP_TYPE}
        value={type}
        onChange={option => onChange({ snap: { ...values, type: option?.value } })}
        variant='accent'
        clearable
      />
    </ListContent>
  </ListItem>
</>

const Size = ({ values: { margin, padding }, onChange }) => <>
  <ListItem>
    <CustomListLabel title='Margin' hint='Adds extra offset when the element is scrolled into view.' />
    <ListContent>
      <PositionInputs values={margin} onChange={({ item, value }) => onChange({ margin: { ...margin, [item]: value } })} />
    </ListContent>
  </ListItem>

  <ListItem>
    <CustomListLabel title='Padding' hint='Defines the safe area around the scroll container’s snap positions.' />
    <ListContent>
      <PositionInputs values={padding} onChange={({ item, value }) => onChange({ padding: { ...padding, [item]: value } })} />
    </ListContent>
  </ListItem>
</>

const CustomListLabel = ({ title, hint }) => <>
  <ListLabel>
    <p>{title}</p>
    <DefaultTooltip side='right' contentProps={{ className: 'max-w-40' }} content={hint}>
      <MessageCircleQuestion size='12' />
    </DefaultTooltip>
  </ListLabel>
</>

const InputTrigger = ({ onClick, value = {} }) => {
  const inputValue = useMemo(() => [
    value?.behavior && `behavior: ${value.behavior}`,
    value?.snap?.align && `snap-align: ${value.snap.align}`,
    value?.snap?.stop && `snap-stop: ${value.snap.stop}`,
    value?.snap?.type && `snap-type: ${value.snap.type}`,
  ].filter(Boolean).join(', '), [value])

  return <Input value={inputValue} readOnly variant='accent' onClick={onClick} />
}

const SNAP_ALIGN = [
  { label: 'Start', value: 'start' },
  { label: 'Center', value: 'center' },
  { label: 'End', value: 'end' },
  { label: 'None', value: 'align-none' },
]

const SNAP_TYPE = [
  { label: 'Both', value: 'both' },
  { label: 'X', value: 'x' },
  { label: 'Y', value: 'y' },
  { label: 'Mandatory', value: 'mandatory' },
  { label: 'Proximity', value: 'proximity' },
  { label: 'None', value: 'none' },
]
